import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import * as Device from 'expo-device';

// Helper function to determine if running on a physical device
const isPhysicalDevice = (): boolean => {
  if (Platform.OS === 'web') return false;

  return Device.isDevice;
};

/**
 * Secure device storage backed by expo-secure-store on physical devices (iOS Keychain /
 * Android Keystore). On simulators and web, AsyncStorage is used instead.
 *
 * **Use only for sensitive data** (e.g. auth credentials in AuthProvider). For preferences
 * and other non-sensitive settings, use AsyncStorage directly — SecureStore persists across
 * app reinstalls on iOS, which is undesirable for ordinary settings.
 *
 * TODO: Refactor to expose separate secure and non-secure APIs so callers do not need to
 * choose the underlying store themselves.
 */
class StorageService {
  async getItem(key: string): Promise<string | null> {
    if (isPhysicalDevice()) {
      try {
        return await SecureStore.getItemAsync(key);
      } catch (error) {
        console.warn('SecureStore error, falling back to AsyncStorage:', error);
        return await AsyncStorage.getItem(key);
      }
    } else {
      return await AsyncStorage.getItem(key);
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    if (isPhysicalDevice()) {
      try {
        await SecureStore.setItemAsync(key, value);
      } catch (error) {
        console.warn('SecureStore error, falling back to AsyncStorage:', error);
        await AsyncStorage.setItem(key, value);
      }
    } else {
      await AsyncStorage.setItem(key, value);
    }
  }

  async removeItem(key: string): Promise<void> {
    if (isPhysicalDevice()) {
      try {
        await SecureStore.deleteItemAsync(key);
      } catch (error) {
        console.warn('SecureStore error, falling back to AsyncStorage:', error);
        await AsyncStorage.removeItem(key);
      }
    } else {
      await AsyncStorage.removeItem(key);
    }
  }
}

export default new StorageService();
